/**
 * Request (Cancellation) Manager
 *
 * Note:
 * Cancelling requests does NOT guarantee that the HTTP request doesn't end up getting sent to the server.
 * There's no way to cancel an HTTP request in general once the request has already been sent over the network.
 * If Axios has already sent the request, all cancel does is cause your Axios request to error out and ignore
 * any response the server sends after cancellation.
 */

const pendingRequests = {};

/**
 * Adds the pending request
 * If the requestId exists and it's not part of a batch, the existing request is 'cancelled' and replaced.
 * If the requestId exists and IS part of a batch, the existing request is added to the batch.
 * @param requestId - unique request id
 * @param cancelFn - Axios cancel fn
 */
export function add(requestId: string, cancelFn: AbortController): void {
  const isBatch = requestId.slice(-2) === '[]';

  // Cancel the existing pending request if not a collection, then replace it.
  if (has(requestId) && !isBatch) {
    cancel(requestId);
  }

  pendingRequests[requestId] = pendingRequests[requestId] || [];
  pendingRequests[requestId].push(cancelFn);
}

/**
 * Removes a tracked request
 * Should occur when completed or cancelled
 * @param requestId - unique request id
 */
export function remove(requestId: string): void {
  delete pendingRequests[requestId];
}

/**
 * Cancels any requests under the provided request id
 * @param requestId - unique request id
 */
export function cancel(requestId: string): void {
  if (has(requestId)) {
    pendingRequests[requestId].forEach((controller) => controller.abort());
  }

  remove(requestId);
}

/**
 * Cancels all requests
 */
export function cancelAll(): void {
  for (const requestId in pendingRequests) {
    cancel(requestId);
  }
}

/**
 * Verifies if the request id already exists
 * @param requestId - unique request id
 */
export function has(requestId: string): boolean {
  return !!pendingRequests[requestId];
}

/**
 * Verifies if the request id is part of a batch of requests
 * The format `name[]` indicates the request is part of batch
 * @param requestId - unique request id
 */
export function isBatch(requestId: string): boolean {
  return requestId.slice(-2) === '[]';
}
